import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from './i18n/index';
import { 
  Home, Dumbbell, Compass, ShoppingBag, 
  User as UserIcon, Zap, ShieldCheck, 
  TrendingUp, Gift, Watch, Loader, LogOut, Menu,
  CreditCard, Lock, BarChart3, Target, Bell, Settings as SettingsIcon,
  Camera, MessageSquare
} from 'lucide-react';
import { doc, collection, query, where, getDoc, DocumentSnapshot, QuerySnapshot } from 'firebase/firestore';
import { safeOnSnapshot } from './utils/safeFirestore';
import { auth, db, onAuthStateChanged, signOut } from './firebaseConfig';
import { SupportedLanguage, User, View } from './types';
import { LOGO_URL } from './constants';
import { initiateCheckout } from './services/stripeService';
import { addDailyStamp } from './services/calendarService';

import { AuthScreen } from './Auth';
import { GamificationDashboard } from './GamificationDashboard';
import { WearableDashboard } from './WearableDashboard';
import { ShopView } from './Shop';
import { ReferralDashboard } from './ReferralDashboard';
import { AICoachView } from './AICoachView';
import { SocialFeedView } from './SocialFeedView';
import { ActiveMapsView } from './ActiveMapsView';
import { ProfileView } from './ProfileView';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AdminDashboard } from './AdminDashboard';
import { ConversationsView } from './ConversationsView';
import { ChatThreadView } from './ChatThreadView';
import { CreateGroupView } from './CreateGroupView';
import { GroupInfoView } from './GroupInfoView';
import { QuickLogFAB } from './components/QuickLogFAB';
import { OnboardingView } from './OnboardingView';
import { PlansView } from './PlansView';
import { ReportsDashboard } from './ReportsDashboard';
import { GoalsDashboard } from './GoalsDashboard';
import { NotificationCenter } from './components/NotificationCenter';
import { NotificationSettingsView } from './components/NotificationSettings';
import { ProgressDashboard } from './ProgressDashboard';
import { LanguageSelector } from './components/LanguageSelector';
import { FeedbackForm } from './components/FeedbackForm';
import { LegalPage } from './components/LegalPages';
import { ContactPage } from './components/ContactPage';
import { Footer } from './components/Footer';

const App = () => {
    const { t, i18n: i18nInstance } = useTranslation();
    const [lang, setLang] = useState<SupportedLanguage>(() => {
        const saved = localStorage.getItem('vitacore_lang');
        return (saved as SupportedLanguage) || SupportedLanguage.EN;
    });

    const [view, setView] = useState<View | string>('dashboard');
    const [user, setUser] = useState<User | null>(null);
    const [authLoading, setAuthLoading] = useState(true);
    const [authReady, setAuthReady] = useState(false); // 🔑 CRITICAL: Only true when Firebase auth is resolved
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isNotifOpen, setIsNotifOpen] = useState(false);
    const [showFeedback, setShowFeedback] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [chatState, setChatState] = useState<{ conversationId: string; otherUserId: string } | null>(null);
    const [groupInfoState, setGroupInfoState] = useState<string | null>(null);

    useEffect(() => {
        if (i18n.isInitialized) {
            i18n.changeLanguage(lang);
            localStorage.setItem('vitacore_lang', lang);
        }
    }, [lang]);

    useEffect(() => {
        // CRITICAL: Only process URL params after auth is resolved
        if (authLoading) return;
        
        const params = new URLSearchParams(window.location.search);
        if (params.get('upgrade') === 'success') {
          // Only process if user is authenticated
          if (!user) {
            // Only log in development - production console should be clean
            if (import.meta.env.DEV) {
              console.warn('[AUTH] Cannot process upgrade success: user not authenticated');
            }
            return;
          }
          
          alert(t('plans.payment_success'));
          window.history.replaceState({}, document.title, window.location.pathname);
          
          // Process referral rewards if user has a paid subscription
          // This is called after Stripe redirect, but webhook should be the primary source
          // TODO: In production, referral processing should happen in webhook for security
          if (user.plan === 'pro' || user.plan === 'unlimited') {
            import('./services/referralService').then(({ processReferralOnSubscription }) => {
              // TypeScript type narrowing: ensure plan is 'pro' | 'unlimited' before calling
              if (user.plan === 'pro' || user.plan === 'unlimited') {
                processReferralOnSubscription(user.id, user.plan).catch(err => {
                  console.error('[APP] Error processing referral:', err);
                });
              }
            });
          }
        }
    }, [user, authLoading, t]);

    useEffect(() => {
        let unsubUser: (() => void) | null = null;
        let unsubNotifs: (() => void) | null = null;
        let hasReceivedFirstSnapshot = false;
        
        // Only log in development
        if (import.meta.env.DEV) {
          console.log('[AUTH] Setting up auth state listener...');
        }
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            setAuthReady(true); // 🔑 CRITICAL: Firebase has determined auth state
            // Only log in development
            if (import.meta.env.DEV) {
              console.log('[AUTH] Auth state changed:', firebaseUser ? `User: ${firebaseUser.uid}` : 'No user');
            }
            // Reset flag for new auth state
            hasReceivedFirstSnapshot = false;
            
            // CRITICAL: Cleanup previous subscriptions if they exist
            // Always verify they are functions before calling
            if (unsubUser && typeof unsubUser === 'function') {
                unsubUser();
                unsubUser = null;
            }
            if (unsubNotifs && typeof unsubNotifs === 'function') {
                unsubNotifs();
                unsubNotifs = null;
            }
            
            if (firebaseUser) {
                const userRef = doc(db, "users", firebaseUser.uid);
                
                // CRITICAL FIX: Use getDoc first to check permissions before setting up onSnapshot
                // This prevents "Uncaught Error in snapshot listener" by verifying access first
                // Firestore can throw errors asynchronously that bypass error handlers
                getDoc(userRef)
                  .then((docSnap: DocumentSnapshot) => {
                    // Only set up real-time listener if we have permission to read
                    try {
                      if (docSnap.exists()) {
                        const userData = docSnap.data();
                        if (userData) {
                          setUser({ ...userData, id: docSnap.id } as User);
                        } else {
                          setUser(null);
                        }
                      } else {
                        setUser(null);
                      }
                      
                      // Mark that we've received the first snapshot
                      if (!hasReceivedFirstSnapshot) {
                        hasReceivedFirstSnapshot = true;
                        setAuthLoading(false);
                      }
                      
                      // Now set up real-time listener (we know we have permission)
                      try {
                        unsubUser = safeOnSnapshot(
                          userRef,
                          (snap: DocumentSnapshot | QuerySnapshot) => {
                            try {
                              // Type guard: check if it's a DocumentSnapshot
                              if ('exists' in snap && snap.exists()) {
                                const userData = snap.data();
                                if (userData) {
                                  setUser({ ...userData, id: snap.id } as User);
                                } else {
                                  setUser(null);
                                }
                              } else {
                                setUser(null);
                              }
                            } catch (snapshotError: any) {
                              // CRITICAL: Handle errors inside snapshot callback
                              if (import.meta.env.DEV) {
                                console.error("[AUTH] Error processing snapshot data:", snapshotError);
                              }
                              try {
                                setUser(null);
                              } catch {
                                // Silently ignore setState errors
                              }
                            }
                          },
                          (err: any) => {
                            // CRITICAL: Handle ALL errors gracefully
                            try {
                              if (import.meta.env.DEV && err?.code !== 'permission-denied') {
                                console.error("[AUTH] Firestore Error in user snapshot:", err);
                              }
                              try {
                                setUser(null);
                              } catch {
                                // Silently ignore
                              }
                            } catch {
                              // Even error handler errors must not throw
                            }
                            return;
                          }
                        );
                      } catch (snapshotInitError: any) {
                        // Handle errors during onSnapshot initialization
                        if (import.meta.env.DEV) {
                          console.error("[AUTH] Error initializing snapshot listener:", snapshotInitError);
                        }
                        unsubUser = null;
                      }
                    } catch (setStateError: any) {
                      // Handle setState errors
                      if (import.meta.env.DEV) {
                        console.error("[AUTH] Error setting user state:", setStateError);
                      }
                      try {
                        setUser(null);
                        if (!hasReceivedFirstSnapshot) {
                          hasReceivedFirstSnapshot = true;
                          setAuthLoading(false);
                        }
                      } catch {
                        // Silently ignore
                      }
                    }
                  })
                  .catch((error: any) => {
                    // CRITICAL: Handle permission-denied and other errors from getDoc
                    // This prevents "Uncaught Error" by catching it here
                    if (error?.code === 'permission-denied') {
                      if (import.meta.env.DEV) {
                        console.warn("[AUTH] Permission denied accessing user document - this may be normal");
                      }
                    } else {
                      if (import.meta.env.DEV) {
                        console.error("[AUTH] Error loading user document:", error);
                      }
                    }
                    // Set user to null and mark auth as resolved
                    try {
                      setUser(null);
                      if (!hasReceivedFirstSnapshot) {
                        hasReceivedFirstSnapshot = true;
                        setAuthLoading(false);
                      }
                    } catch {
                      // Silently ignore setState errors
                    }
                    unsubUser = null;
                  });
                
                // Only set up notifications after user document is confirmed
                // This prevents premature Firestore calls
                const notifQuery = query(collection(db, "users", firebaseUser.uid, "notifications"), where("isRead", "==", false));
                unsubNotifs = safeOnSnapshot(notifQuery, (snap) => {
                  try {
                    // Type guard: check if it's a QuerySnapshot
                    if ('size' in snap) {
                      setUnreadCount(snap.size);
                    } else {
                      setUnreadCount(0);
                    }
                  } catch (snapshotError: any) {
                    // CRITICAL: Handle errors inside snapshot callback - don't let them crash the app
                    if (import.meta.env.DEV) {
                      console.error("[AUTH] Error processing notifications snapshot:", {
                        code: snapshotError?.code,
                        message: snapshotError?.message
                      });
                    }
                    setUnreadCount(0);
                  }
                }, (err: any) => {
                  // CRITICAL: Handle errors gracefully - don't crash the app
                  // Wrap in try-catch to prevent any errors from propagating
                  try {
                    if (err?.code === 'permission-denied') {
                      // This is a configuration issue - log once in dev, silent in prod
                      if (import.meta.env.DEV) {
                        console.warn("[AUTH] Permission denied accessing notifications - this is normal if rules restrict access");
                      }
                    } else {
                      // Real errors should be logged in both dev and prod
                      if (import.meta.env.DEV) {
                        console.error("[AUTH] Notifications query error:", err);
                      }
                    }
                    setUnreadCount(0);
                  } catch (errorHandlerError: any) {
                    // CRITICAL: Even the error handler can't throw - catch everything
                    if (import.meta.env.DEV) {
                      console.error("[AUTH] Error in notifications error handler (this should never happen):", errorHandlerError);
                    }
                    setUnreadCount(0);
                  }
                  // CRITICAL: Do NOT throw - this would crash React
                  return;
                });

                // Add daily calendar stamp (fire and forget, don't block) - only after auth is confirmed
                addDailyStamp(firebaseUser.uid, 'standard').catch(err => {
                  console.error("Error adding daily stamp:", err);
                });
            } else {
                // No user authenticated - clear state and mark auth as resolved
                setUser(null);
                setUnreadCount(0);
            setAuthLoading(false);
            }
        });
        
        // CRITICAL: Safe cleanup - only call unsubscribe if it's actually a function
        // This prevents "r is not a function" errors
        return () => {
            if (typeof unsubscribe === 'function') {
                unsubscribe();
            }
            if (typeof unsubUser === 'function') {
                unsubUser();
            }
            if (typeof unsubNotifs === 'function') {
                unsubNotifs();
            }
        };
    }, []);

    const handleUpgrade = async (planId: string) => {
      // CRITICAL: Guard against null user
      if (!user || authLoading) {
        // Only log in development - production console should be clean
        if (import.meta.env.DEV) {
          console.warn('[AUTH] Cannot upgrade plan: user not authenticated');
        }
        return;
      }
      await initiateCheckout(planId, user.id);
    };

    if (authLoading) return (
      <div className="min-h-screen bg-[#0a0a0f] flex flex-col items-center justify-center gap-4">
        <Loader className="animate-spin text-cyan-500 w-12 h-12" />
        <p className="text-cyan-500/50 text-[10px] font-black tracking-widest uppercase animate-pulse">{t('app.initializing')}</p>
      </div>
    );

    if (!user) return <AuthScreen lang={lang} setLang={setLang} onLoginSuccess={setUser} />;

    if (!user.hasOnboarded) {
      return <OnboardingView user={user} lang={lang} onComplete={() => setView('plans')} />;
    }

    const checkAccess = (targetView: View): boolean => {
      // CRITICAL: Never allow access if user is null
      if (!user) return false;
      
      // Admin hanno accesso completo a tutte le funzionalità
      if (user.role === 'admin') return true;
      
      const plan = user.plan || 'free';
      const restricted = ['wearable', 'map', 'reports', 'goals', 'coach', 'progress'];
      if (restricted.includes(targetView)) return plan !== 'free';
      return true;
    };

    const handleViewChange = (targetView: View | string) => {
      // CRITICAL: Prevent navigation if user is null
      if (!user) {
        // Only log in development - production console should be clean
        if (import.meta.env.DEV) {
          console.warn('[AUTH] Attempted navigation without authenticated user');
        }
        return;
      }
      
      if (typeof targetView === 'string' && targetView.startsWith('profile:')) {
        setView(targetView);
        setIsMenuOpen(false);
        setIsNotifOpen(false);
        setChatState(null);
        return;
      }
      if (targetView === 'messages') {
        setView('messages');
        setIsMenuOpen(false);
        setIsNotifOpen(false);
        setChatState(null);
        setGroupInfoState(null);
        return;
      }
      if (!checkAccess(targetView as View)) setView('plans');
      else setView(targetView as View);
      setIsMenuOpen(false);
      setIsNotifOpen(false);
      setChatState(null);
    };

    // CRITICAL: Guard against null user - this code only runs after user check, but add safety
    if (!user) {
      // This should never happen due to early return, but TypeScript safety
      return <AuthScreen lang={lang} setLang={setLang} onLoginSuccess={setUser} />;
    }

    // Admin hanno accesso completo - nessun feature locked
    const isAdmin = user.role === 'admin';
    
    const navItems = [
        { id: 'dashboard', icon: <TrendingUp size={20}/>, label: t('nav.dash') },
        { id: 'progress', icon: <Camera size={20}/>, label: t('nav.progress'), locked: !isAdmin && user.plan === 'free' },
        { id: 'goals', icon: <Target size={20}/>, label: t('nav.goals'), locked: !isAdmin && user.plan === 'free' },
        { id: 'reports', icon: <BarChart3 size={20}/>, label: t('nav.reports'), locked: !isAdmin && user.plan === 'free' },
        { id: 'feed', icon: <Home size={20}/>, label: t('nav.feed') },
        { id: 'coach', icon: <Zap size={20}/>, label: t('nav.coach'), locked: !isAdmin && user.plan === 'free' },
        { id: 'map', icon: <Compass size={20}/>, label: t('nav.map'), locked: !isAdmin && user.plan === 'free' },
        { id: 'wearable', icon: <Watch size={20}/>, label: t('wearable.smartwatch'), highlight: true, locked: !isAdmin && user.plan === 'free' },
        { id: 'shop', icon: <ShoppingBag size={20}/>, label: t('nav.shop') },
        { id: 'referral', icon: <Gift size={20}/>, label: t('nav.referral') },
        { id: 'messages', icon: <MessageSquare size={20}/>, label: t('chat.conversations') },
    ];

    if (user.role === 'admin') navItems.push({ id: 'admin', icon: <ShieldCheck size={20}/>, label: t('admin.ops') });

    const NavContent = () => (
        <div className="flex flex-col h-full overflow-y-auto tech-grid">
            <div className="flex-shrink-0 flex items-center gap-3 mb-14 px-2 group cursor-pointer" onClick={() => setView('dashboard')}>
                <img src={LOGO_URL} alt="Logo" className="w-12 h-12 object-contain transition-all duration-500 group-hover:rotate-12 group-hover:scale-110" />
                <span className="text-2xl font-light tracking-[0.25em] bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-blue-500 to-cyan-400 drop-shadow-[0_0_10px_rgba(0,255,255,0.3)]">VITACORE</span>
            </div>
            
            <nav className="flex-1 space-y-1 min-h-0 overflow-y-auto pr-2">
                {navItems.map(item => (
                    <button 
                        key={item.id}
                        onClick={() => handleViewChange(item.id as View)}
                        className={`w-full flex items-center justify-between gap-4 px-5 py-4 rounded-2xl font-black transition-all group active:scale-95 ${
                            view === item.id 
                                ? 'bg-cyan-500 text-black shadow-[0_0_30px_rgba(0,255,255,0.4)]' 
                                : item.highlight ? 'text-cyan-400 hover:bg-cyan-500/10' : 'text-slate-500 hover:text-white hover:bg-slate-900'
                        }`}
                    >
                        <div className="flex items-center gap-4">
                          <div className={view === item.id ? 'text-black' : item.highlight ? 'text-cyan-400' : 'text-slate-500 group-hover:text-cyan-400 transition-colors'}>
                            {item.icon}
                          </div>
                          <span className="text-[10px] uppercase tracking-[0.2em] font-black">{item.label}</span>
                        </div>
                        {item.locked && <Lock size={12} className="text-slate-800 group-hover:text-cyan-400/50 transition-colors" />}
                    </button>
                ))}
            </nav>

            <div className="flex-shrink-0 mt-auto p-6 bg-[#0a0a0f]/50 backdrop-blur-sm rounded-[2.5rem] border border-slate-800/50 shadow-[0_20px_50px_rgba(0,0,0,0.5)] space-y-5">
                <button 
                  onClick={() => handleViewChange('settings')}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-[9px] font-black uppercase tracking-[0.3em] transition-all group ${view === 'settings' ? 'text-cyan-400 bg-cyan-500/10' : 'text-slate-600 hover:text-white'}`}
                >
                  <SettingsIcon size={16} className="group-hover:rotate-90 transition-transform" /> {t('nav.settings')}
                </button>
                <div 
                  className="flex items-center gap-4 px-2 cursor-pointer hover:bg-slate-800/50 rounded-xl p-2 transition-colors"
                  onClick={() => handleViewChange(`profile:${user.id}`)}
                  title={t('profile.go_to_profile')}
                >
                    <img src={user.avatar} className="w-12 h-12 rounded-2xl border-2 border-cyan-500 shadow-[0_0_20px_rgba(0,255,255,0.2)]" alt="Avatar" />
                    <div className="min-w-0 flex-1">
                        <p className="font-black text-[11px] truncate text-white uppercase tracking-tighter">{user.name}</p>
                        {user.username && (
                          <p className="text-[8px] text-cyan-400 font-bold">@{user.username}</p>
                        )}
                        <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                            <p className="text-[8px] font-black uppercase text-cyan-400 tracking-widest">{user.plan}</p>
                        </div>
                    </div>
                </div>
                <button onClick={() => signOut(auth)} className="w-full flex items-center gap-2 justify-center py-2 text-slate-700 hover:text-orange-500 text-[8px] font-black uppercase tracking-[0.4em] transition-all hover:tracking-[0.5em]">
                    <LogOut size={14}/> {t('settings.sign_out')}
                </button>
            </div>
        </div>
    );

    return (
        <div className="bg-[#0a0a0f] min-h-screen text-slate-100 font-sans selection:bg-cyan-500/30 selection:text-white flex flex-col">
            <LanguageSelector lang={lang} setLang={setLang} />
            <aside className="hidden md:block fixed left-0 top-0 bottom-0 w-80 bg-[#0a0a0f] border-r border-slate-900/50 z-50 shadow-2xl">
                <NavContent />
            </aside>

            <header className="fixed top-0 left-0 md:left-80 right-0 h-24 bg-[#0a0a0f]/90 backdrop-blur-3xl border-b border-slate-900/50 z-[60] flex items-center justify-between px-10">
                <div className="flex md:hidden items-center gap-3">
                    <img src={LOGO_URL} alt="Logo" className="w-12 h-12 object-contain" />
                    <span className="text-xl font-light tracking-[0.3em] bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-500">VITACORE</span>
                </div>
                <div className="hidden md:block">
                  <div className="flex items-center gap-6">
                    <div className="text-[9px] font-black text-slate-700 uppercase tracking-[0.4em] flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-cyan-500 shadow-[0_0_8px_rgba(0,255,255,0.5)]" />
                        SYNC STATUS: <span className="text-cyan-400">ENCRYPTED</span>
                    </div>
                    <div className="h-4 w-[1px] bg-slate-800" />
                    <div className="text-[9px] font-black text-slate-700 uppercase tracking-[0.4em]">GRID: <span className="text-white">ALPHA-V</span></div>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <button onClick={() => setIsNotifOpen(!isNotifOpen)} className="relative p-3.5 bg-[#050505] border border-slate-800 rounded-2xl text-slate-600 hover:text-cyan-400 transition-all shadow-xl group">
                    <Bell size={22} className="group-hover:animate-bounce" />
                    {unreadCount > 0 && <span className="absolute -top-1 -right-1 w-6 h-6 bg-cyan-500 text-black text-[10px] font-black flex items-center justify-center rounded-xl border-4 border-black shadow-lg shadow-cyan-500/30">{unreadCount > 9 ? '9+' : unreadCount}</span>}
                  </button>
                  {user && (
                    <button 
                      onClick={() => setShowFeedback(true)} 
                      className="hidden md:flex items-center gap-2 px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-xl text-slate-400 hover:text-emerald-400 hover:border-emerald-500/50 transition-all text-xs font-black uppercase tracking-widest"
                      title={t('nav.feedback')}
                    >
                      <MessageSquare size={16} />
                      {t('nav.feedback')}
                    </button>
                  )}
                  <button onClick={() => setIsMenuOpen(true)} className="md:hidden p-2 text-slate-500"><Menu/></button>
                </div>
            </header>

            {isNotifOpen && (
              <div className="fixed inset-0 z-[100]">
                 <div className="absolute inset-0 bg-[#0a0a0f]/80 backdrop-blur-xl" onClick={() => setIsNotifOpen(false)} />
                 <NotificationCenter user={user} authReady={authReady} onClose={() => setIsNotifOpen(false)} setView={setView} t={t} />
              </div>
            )}

            {isMenuOpen && (
                <div className="fixed inset-0 z-[100] md:hidden">
                    <div className="absolute inset-0 bg-[#0a0a0f]/95 backdrop-blur-3xl animate-fadeIn" onClick={() => setIsMenuOpen(false)} />
                    <div className="absolute left-0 top-0 bottom-0 w-80 bg-[#0a0a0f] shadow-2xl animate-slideInLeft border-r border-slate-800">
                        <NavContent />
                    </div>
                </div>
            )}

            <main className="md:pl-80 pt-24 flex-1 relative tech-grid overflow-x-hidden">
                <div className="animate-fadeIn max-w-[1600px] mx-auto min-h-full relative z-10">
                    {view === 'dashboard' && <GamificationDashboard user={user} authReady={authReady} />}
                    {view === 'progress' && <ProgressDashboard user={user} />}
                    {view === 'goals' && <GoalsDashboard user={user} authReady={authReady} />}
                    {view === 'reports' && <ReportsDashboard user={user} />}
                    {view === 'wearable' && <WearableDashboard user={user} authReady={authReady} />}
                    {view === 'shop' && <ShopView t={t} user={user} authReady={authReady} />}
                    {view === 'referral' && <ReferralDashboard user={user} />}
                    {view === 'coach' && <AICoachView user={user} lang={lang} authReady={authReady} />}
                    {view === 'feed' && <SocialFeedView user={user} setView={setView} authReady={authReady} />}
                    {typeof view === 'string' && view.startsWith('profile:') && (
                      <ErrorBoundary>
                        <ProfileView 
                          userId={view.split(':')[1]} 
                          currentUser={user} 
                          authReady={authReady}
                          onBack={() => setView('feed')}
                          onNavigateToChat={(conversationId, otherUserId) => {
                            setChatState({ conversationId, otherUserId });
                            setView('chat');
                          }}
                        />
                      </ErrorBoundary>
                    )}
                    {view === 'messages' && (
                      <ConversationsView
                        user={user}
                        authReady={authReady}
                        onBack={() => setView('feed')}
                        onSelectConversation={(conversationId, otherUserId) => {
                          setChatState({ conversationId, otherUserId });
                          setView('chat');
                        }}
                        onCreateGroup={() => {
                          setView('create-group');
                        }}
                      />
                    )}
                    {view === 'create-group' && (
                      <CreateGroupView
                        user={user}
                        onBack={() => setView('messages')}
                        onGroupCreated={(conversationId, otherUserId) => {
                          setChatState({ conversationId, otherUserId });
                          setView('chat');
                        }}
                        t={t}
                      />
                    )}
                    {view === 'chat' && chatState && (
                      <ChatThreadView
                        user={user}
                        authReady={authReady}
                        conversationId={chatState.conversationId}
                        otherUserId={chatState.otherUserId}
                        onBack={() => {
                          setView('messages');
                          setChatState(null);
                        }}
                        onShowGroupInfo={() => {
                          setGroupInfoState(chatState.conversationId);
                          setView('group-info');
                        }}
                        t={t}
                      />
                    )}
                    {view === 'group-info' && groupInfoState && (
                      <GroupInfoView
                        user={user}
                        authReady={authReady}
                        conversationId={groupInfoState}
                        onBack={() => {
                          setView('chat');
                          setGroupInfoState(null);
                        }}
                      />
                    )}
                    {view === 'map' && <ActiveMapsView user={user} lang={lang} />}
                    {view === 'plans' && <PlansView user={user} t={t} onUpgrade={handleUpgrade} onNavigate={(view) => setView(view)} />}
                    {view === 'settings' && <NotificationSettingsView user={user} onBack={() => setView('dashboard')} lang={lang} setLang={setLang} currentView={view} />}
                    {view === 'admin' && user.role === 'admin' && <AdminDashboard />}
                    {view === 'privacy' && <LegalPage onBack={() => setView('dashboard')} type="privacy" />}
                    {view === 'terms' && <LegalPage onBack={() => setView('dashboard')} type="terms" />}
                    {view === 'refund' && <LegalPage onBack={() => setView('dashboard')} type="refund" />}
                    {view === 'contact' && <ContactPage onBack={() => setView('dashboard')} />}
                </div>
                <QuickLogFAB user={user} />
            </main>
            <Footer onNavigate={(view) => setView(view)} />

            {/* Global Feedback Modal */}
            {showFeedback && user && (
              <FeedbackForm
                user={user}
                onClose={() => setShowFeedback(false)}
                initialView={typeof view === 'string' ? view : 'dashboard'}
                initialPlan={user.plan}
                initialLanguage={lang}
              />
            )}
        </div>
    );
};

export default App;


